import { keepPreviousData, queryOptions, useQuery } from "@tanstack/react-query"
import { api } from "@/api/client"
import type { InsightsBreakdown } from "@/api/generated/InsightsBreakdown"
import type { InsightsOverview } from "@/api/generated/InsightsOverview"
import type { InsightsTools } from "@/api/generated/InsightsTools"
import type { PlatformSeries } from "@/api/generated/PlatformSeries"
import type { PlatformStats } from "@/api/generated/PlatformStats"

/** Reporting periods understood by `/insights/*` (`custom` needs `start` + `end`). */
export const INSIGHTS_PERIODS = [
  "today",
  "24h",
  "7d",
  "30d",
  "90d",
  "this_month",
  "last_month",
  "custom",
] as const
export type InsightsPeriod = (typeof INSIGHTS_PERIODS)[number]

export const BREAKDOWN_DIMENSIONS = ["agent", "model", "user", "source"] as const
export type BreakdownDimension = (typeof BREAKDOWN_DIMENSIONS)[number]

/** Sliding windows of the cross-tenant platform dashboard. */
export const PLATFORM_WINDOWS = ["1h", "6h", "24h", "today", "3d", "7d", "14d", "30d", "90d", "365d"] as const
export type PlatformWindow = (typeof PLATFORM_WINDOWS)[number]

/** Shared filter of every tenant usage query. */
export interface InsightsFilters {
  period: InsightsPeriod
  /** ISO date/datetime; only with `period: "custom"`. */
  start?: string
  end?: string
  agent_id?: number
  source?: string
  user_id?: number
  model_id?: number
}

export const insightsKeys = {
  all: ["insights"] as const,
  overview: (f: InsightsFilters) => [...insightsKeys.all, "overview", f] as const,
  breakdown: (f: InsightsFilters, dimension: BreakdownDimension) =>
    [...insightsKeys.all, "breakdown", dimension, f] as const,
  tools: (f: InsightsFilters) => [...insightsKeys.all, "tools", f] as const,
  platform: (window: string) => [...insightsKeys.all, "platform", window] as const,
  platformSeries: (since: string, until: string) =>
    [...insightsKeys.all, "platform-series", since, until] as const,
}

const params = (f: InsightsFilters) => ({
  period: f.period,
  start: f.period === "custom" ? f.start : undefined,
  end: f.period === "custom" ? f.end : undefined,
  agent_id: f.agent_id,
  source: f.source,
  user_id: f.user_id,
  model_id: f.model_id,
})

export const insightsOverviewQuery = (f: InsightsFilters) =>
  queryOptions({
    queryKey: insightsKeys.overview(f),
    queryFn: ({ signal }) => api.get<InsightsOverview>("/insights/overview", { signal, query: params(f) }),
    placeholderData: keepPreviousData,
  })

export const useInsightsOverview = (f: InsightsFilters, enabled = true) =>
  useQuery({ ...insightsOverviewQuery(f), enabled })

export const insightsBreakdownQuery = (f: InsightsFilters, dimension: BreakdownDimension) =>
  queryOptions({
    queryKey: insightsKeys.breakdown(f, dimension),
    queryFn: ({ signal }) =>
      api.get<InsightsBreakdown>("/insights/breakdown", {
        signal,
        query: { ...params(f), dimension, limit: 100 },
      }),
    placeholderData: keepPreviousData,
  })

export const useInsightsBreakdown = (f: InsightsFilters, dimension: BreakdownDimension, enabled = true) =>
  useQuery({ ...insightsBreakdownQuery(f, dimension), enabled })

export const insightsToolsQuery = (f: InsightsFilters) =>
  queryOptions({
    queryKey: insightsKeys.tools(f),
    queryFn: ({ signal }) =>
      api.get<InsightsTools>("/insights/tools", { signal, query: { ...params(f), limit: 100 } }),
    placeholderData: keepPreviousData,
  })

export const useInsightsTools = (f: InsightsFilters, enabled = true) =>
  useQuery({ ...insightsToolsQuery(f), enabled })

export const platformStatsQuery = (window: string) =>
  queryOptions({
    queryKey: insightsKeys.platform(window),
    queryFn: ({ signal }) => api.get<PlatformStats>("/insights/platform", { signal, query: { window } }),
    placeholderData: keepPreviousData,
  })

/** Cross-tenant statistics (platform admins only). */
export const usePlatformStats = (
  window: string,
  opts: { enabled?: boolean; refetchInterval?: number | false } = {},
) =>
  useQuery({
    ...platformStatsQuery(window),
    enabled: opts.enabled ?? true,
    refetchInterval: opts.refetchInterval,
  })

/** Drill-down series for an arbitrary range (bucket chosen by the server from the span). */
export const platformSeriesQuery = (since: string, until: string) =>
  queryOptions({
    queryKey: insightsKeys.platformSeries(since, until),
    queryFn: ({ signal }) =>
      api.get<PlatformSeries>("/insights/platform/series", { signal, query: { since, until } }),
  })

export const usePlatformSeries = (range: { since: string; until: string } | undefined) =>
  useQuery({ ...platformSeriesQuery(range?.since ?? "", range?.until ?? ""), enabled: !!range })
